import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Database,
  Download,
  HardDrive,
  Info,
  RefreshCw,
  Server,
  Timer,
  TrendingUp,
  Zap,
  Filter,
  X,
  Globe,
  Smartphone,
  WifiOff,
  User,
  Eye,
  CheckCircle,
  Clock,
  Copy,
  ExternalLink,
  ChevronRight,
  ArrowLeft,
  Check,
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import toast from 'react-hot-toast';
import {
  ApiLatencyBarChart,
  CategoryPieChart,
  DataTable,
  HealthScoreGauge,
  NotificationChannelChart,
  QueueBarChart,
  ResourceBarChart,
  ServiceHealthPie,
  StatusBadgeInline,
  TrendAreaChart,
  UptimeDonut,
} from '../../components/MasterAdmin/operations/OperationsCharts';
import OperationsPanelShell from '../../components/MasterAdmin/operations/OperationsPanelShell';
import OperationsSidebar, {
  OperationsMobileTabs,
  type OperationsTabId,
} from '../../components/MasterAdmin/operations/OperationsSidebar';
import OperationsStatCard from '../../components/MasterAdmin/operations/OperationsStatCard';
import { OpsChartTitle } from '../../components/MasterAdmin/operations/OpsMetricLabels';
import OperationsInfoTip from '../../components/MasterAdmin/operations/OperationsInfoTip';
import {
  opsFilterClass,
  opsLogViewer,
  opsPageWrap,
  opsPrimaryBtn,
  opsSecondaryBtn,
  opsShell,
  PIE_PALETTE,
} from '../../components/MasterAdmin/operations/operationsUi';
import {
  fetchLiveLogs,
  fetchOperationsDashboard,
  fetchOperationsErrors,
  refreshOperationsDashboard,
  rollbackDeployment,
  statusEmoji,
  updateErrorStatus,
  type OperationsDashboard,
  type RuntimeErrorRow,
} from '../../services/MasterAdmin/operationsService';

const REFRESH_MS = 30_000;

const TAB_META: Record<OperationsTabId, { title: string; description: string }> = {
  overview: { title: 'Overview', description: 'System health score, service map, and live recommendations.' },
  backend: { title: 'Backend & Cloud Run', description: 'CPU, memory, latency, instances, and deployment metadata.' },
  database: { title: 'Database', description: 'Cloud SQL metrics, connections, migrations, and slow queries.' },
  errors: { title: 'Runtime errors', description: 'Grouped errors from Cloud Logging / BigQuery with filters.' },
  apis: { title: 'Internal APIs', description: 'Automated health probes for core endpoints.' },
  external: { title: 'External services', description: 'Firebase, Razorpay, WhatsApp, maps, and third-party latency.' },
  auth: { title: 'Authentication', description: 'JWT, Firebase Auth, and token policy checks.' },
  notifications: { title: 'Notifications', description: 'Push, email, SMS, and WhatsApp delivery stats.' },
  scheduler: { title: 'Scheduler & cron', description: 'Cloud Scheduler jobs and reminder engine.' },
  queue: { title: 'Queues', description: 'Pending, running, and failed queue items.' },
  storage: { title: 'Storage', description: 'Firebase Storage availability and latency.' },
  security: { title: 'Security', description: 'SSL, JWT expiry, credentials, and Secret Manager.' },
  analytics: { title: 'Error analytics', description: 'Category breakdown and top errors by frequency.' },
  logs: { title: 'Live logs', description: 'Stream recent logs with search and export.' },
  deployments: { title: 'Deployments', description: 'Cloud Run revision history and rollback.' },
  uptime: { title: 'Uptime', description: 'Availability over 24h, 7d, and 30d windows.' },
};

function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: string; message?: string; response?: unknown };
  return !err.response && (err.code === 'ERR_NETWORK' || /network error/i.test(String(err.message || '')));
}

const OperationsDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<OperationsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<OperationsTabId>('overview');
  
  // System Error Log Filters
  const [errorRange, setErrorRange] = useState('24h');
  const [errorCategory, setErrorCategory] = useState('ALL');
  const [errorSearch, setErrorSearch] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('ALL');
  const [filterAppName, setFilterAppName] = useState('ALL');
  const [filterStatusCode, setFilterStatusCode] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterUserSearch, setFilterUserSearch] = useState('');
  
  // Pagination & List state
  const [errorPage, setErrorPage] = useState(1);
  const [hasMoreErrors, setHasMoreErrors] = useState(false);
  const [totalErrorsCount, setTotalErrorsCount] = useState(0);
  const [loadingMoreErrors, setLoadingMoreErrors] = useState(false);
  const [errorLogsList, setErrorLogsList] = useState<RuntimeErrorRow[]>([]);
  
  // Detail View & Status Modal state
  const [selectedErrorModal, setSelectedErrorModal] = useState<RuntimeErrorRow | null>(null);
  const [detailViewError, setDetailViewError] = useState<RuntimeErrorRow | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);

  const [filteredErrors, setFilteredErrors] = useState<OperationsDashboard['sections']['runtimeErrors'] | null>(null);
  const [liveLogs, setLiveLogs] = useState<Array<Record<string, unknown>>>([]);
  const [logsNotice, setLogsNotice] = useState<string | null>(null);
  const [logSearch, setLogSearch] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [backendOffline, setBackendOffline] = useState(false);

  const loadDashboard = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true);
      else setLoading(true);
      const data = force ? await refreshOperationsDashboard() : await fetchOperationsDashboard();
      setDashboard(data);
      setBackendOffline(false);
      setLastUpdated(new Date());
    } catch (error) {
      if (isNetworkError(error)) {
        setBackendOffline(true);
        toast.error('Backend not reachable — start vedika-health-backend on port 8080.');
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadErrors = useCallback(async (pageToLoad = 1, append = false) => {
    try {
      if (append) setLoadingMoreErrors(true);
      const data = await fetchOperationsErrors({
        range: errorRange,
        category: errorCategory !== 'ALL' && errorCategory !== '' ? errorCategory : undefined,
        search: errorSearch || undefined,
        platform: filterPlatform !== 'ALL' ? filterPlatform : undefined,
        appName: filterAppName !== 'ALL' ? filterAppName : undefined,
        statusCode: filterStatusCode !== 'ALL' ? filterStatusCode : undefined,
        status: filterStatus !== 'ALL' ? filterStatus : undefined,
        userSearch: filterUserSearch || undefined,
        page: pageToLoad,
        limit: 20,
      });

      const newRows = data.rows || [];
      setTotalErrorsCount(data.total || newRows.length);
      setHasMoreErrors(Boolean(data.hasMore));
      setErrorPage(pageToLoad);

      setErrorLogsList((prev) => {
        const list = append ? [...prev, ...newRows] : newRows;
        setFilteredErrors({
          status: dashboard?.sections.runtimeErrors.status || 'unknown',
          latest: list,
          grouped: data.grouped || [],
          from: dashboard?.sections.runtimeErrors.from || '',
          to: dashboard?.sections.runtimeErrors.to || '',
        });
        return list;
      });
    } catch {
      if (!append) {
        setFilteredErrors(dashboard?.sections.runtimeErrors || null);
        setErrorLogsList(dashboard?.sections.runtimeErrors.latest || []);
      }
    } finally {
      setLoadingMoreErrors(false);
    }
  }, [
    errorRange,
    errorCategory,
    errorSearch,
    filterPlatform,
    filterAppName,
    filterStatusCode,
    filterStatus,
    filterUserSearch,
    dashboard?.sections.runtimeErrors,
  ]);

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(() => loadDashboard(), REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  useEffect(() => {
    if (activeTab !== 'errors') return;
    loadErrors();
  }, [activeTab, loadErrors]);

  const handleStatusChange = async (errorId: string, newStatus: string) => {
    try {
      setUpdatingStatusId(errorId);
      await updateErrorStatus(errorId, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      setErrorLogsList((prev) =>
        prev.map((r) => (r.id === errorId ? { ...r, status: newStatus } : r))
      );
      if (selectedErrorModal && selectedErrorModal.id === errorId) {
        setSelectedErrorModal((prev) => (prev ? { ...prev, status: newStatus } : null));
      }
      if (detailViewError && detailViewError.id === errorId) {
        setDetailViewError((prev) => (prev ? { ...prev, status: newStatus } : null));
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleNavigateProfile = (userId?: string | null, userRole?: string | null) => {
    if (!userId) {
      toast.error('No User or Vendor ID recorded for this error log.');
      return;
    }
    const roleUpper = (userRole || '').toUpperCase();
    if (roleUpper === 'DOCTOR' || userId.startsWith('DOC_')) {
      navigate(`/vendors/doctor-clinic/${userId}/details`);
    } else if (roleUpper === 'AMBULANCE' || userId.startsWith('AMB_')) {
      navigate(`/vendors/ambulance/${userId}/details`);
    } else {
      navigate(`/users/${userId}`);
    }
  };

  useEffect(() => {
    if (activeTab !== 'logs') return;
    (async () => {
      try {
        const result = await fetchLiveLogs({ limit: 80, search: logSearch || undefined });
        setLiveLogs(result.rows || []);
        setLogsNotice(result.notice || null);
      } catch {
        setLiveLogs([]);
        setLogsNotice('Unable to load logs');
      }
    })();
  }, [activeTab, logSearch, dashboard?.generatedAt]);

  const sections = dashboard?.sections;
  const overall = dashboard?.overall;
  const meta = dashboard?.meta;
  const tabMeta = TAB_META[activeTab];

  const categoryChartData = useMemo(
    () => Object.entries(sections?.errorAnalytics.byCategory || {}).map(([name, value]) => ({ name, value })),
    [sections?.errorAnalytics.byCategory]
  );

  const serviceHealthItems = useMemo(() => {
    if (!sections) return [];
    return [
      { name: 'Backend', status: sections.backend.status },
      { name: 'Database', status: sections.database.status },
      { name: 'Internal APIs', status: sections.internalApis.status },
      { name: 'External', status: sections.externalServices.status },
      { name: 'Auth', status: sections.authentication.status },
      { name: 'Notifications', status: sections.notifications.status },
      { name: 'Storage', status: sections.storage.status },
      { name: 'Security', status: sections.security.status },
      { name: 'Queue', status: sections.queue.status },
    ];
  }, [sections]);

  const apiTrendData = useMemo(() => {
    if (!sections) return [];
    return sections.internalApis.endpoints.map((e) => ({
      label: e.name.split(' ')[0],
      value: e.responseTimeMs,
    }));
  }, [sections]);

  const handleRollback = async (revision: string) => {
    if (!window.confirm(`Rollback all traffic to revision ${revision}?`)) return;
    try {
      await rollbackDeployment(revision);
      toast.success(`Rollback initiated for ${revision}`);
      loadDashboard(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Rollback failed');
    }
  };

  const downloadLogs = () => {
    const blob = new Blob([JSON.stringify(liveLogs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vedika-ops-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const refreshBtn = (
    <button type="button" onClick={() => loadDashboard(true)} disabled={refreshing} className={opsPrimaryBtn}>
      <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
      Refresh
    </button>
  );

  if (backendOffline && !dashboard) {
    return (
      <div className={opsPageWrap}>
        <div className={`${opsShell} items-center justify-center p-8`}>
          <div className="max-w-lg rounded-2xl border border-red-200 bg-red-50 p-8 text-center dark:border-red-900 dark:bg-red-950/40">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">Backend not running</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Start <code className="rounded bg-red-100 px-1 dark:bg-red-900">vedika-health-backend</code> on port 8080.
            </p>
            <button type="button" onClick={() => loadDashboard()} className={`${opsPrimaryBtn} mt-6`}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !dashboard) {
    return (
      <div className={`${opsPageWrap} items-center justify-center`}>
        <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const renderPanel = () => {
    if (!sections) return null;

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {meta?.localMode && (
              <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div className="text-sm text-amber-900 dark:text-amber-100">
                  <p className="font-semibold">Local development</p>
                  <p className="mt-1 opacity-90">GCP metrics need ADC. DB, API probes, and auth checks work locally.</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <HealthScoreGauge score={overall?.healthScore ?? 0} label="Platform health" infoKey="healthScore" />
              <ServiceHealthPie title="Services by status" items={serviceHealthItems} infoKey="serviceHealthPie" />
              <CategoryPieChart title="Errors by category" data={categoryChartData} infoKey="errorCategoryPie" />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
              <OperationsStatCard
                label="Cloud Run"
                infoKey="cloudRunStatus"
                value={sections.backend.cloudRunStatus}
                sub={`${statusEmoji(sections.backend.status)} ${sections.backend.status}`}
                icon={Server}
                loading={refreshing}
              />
              <OperationsStatCard
                label="Database"
                infoKey="databaseConnected"
                value={sections.database.dbReady ? 'Connected' : 'Booting'}
                sub={sections.database.queryResponseTimeFormatted || undefined}
                icon={Database}
                loading={refreshing}
              />
              <OperationsStatCard
                label="API availability"
                infoKey="apiAvailability"
                value={`${sections.internalApis.availabilityPercent}%`}
                sub={`${sections.internalApis.endpoints.length} endpoints`}
                icon={Zap}
                loading={refreshing}
              />
              <OperationsStatCard
                label="Uptime 24h"
                infoKey="uptime24h"
                value={sections.uptime.last24Hours.availabilityPercent != null ? `${sections.uptime.last24Hours.availabilityPercent}%` : '—'}
                sub={`${sections.uptime.last24Hours.totalRequests.toLocaleString()} req`}
                icon={TrendingUp}
                loading={refreshing}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <ResourceBarChart
                title="Resource utilization"
                infoKey="resourceUtilization"
                items={[
                  { name: 'CPU', value: sections.backend.cpuUsagePercent },
                  { name: 'Memory', value: sections.backend.memoryUsagePercent },
                  { name: 'DB CPU', value: sections.database.cpuUsagePercent },
                  { name: 'Storage', value: sections.database.storageUsedPercent },
                ]}
              />
              <TrendAreaChart title="Internal API latency (ms)" data={apiTrendData} color="#2563eb" infoKey="apiLatencyTrend" />
            </div>

            {sections.recommendations.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  Recommendations
                  <OperationsInfoTip metricKey="recommendations" title="Recommendations" />
                </h3>
                <div className="space-y-2">
                  {sections.recommendations.map((rec) => (
                    <div
                      key={rec.id}
                      className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900/50"
                    >
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{rec.message}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{rec.action}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'backend':
        return (
          <div className="space-y-6">
            {sections.backend.notice && (
              <p className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/40">
                {sections.backend.notice}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
              <OperationsStatCard label="RPM" infoKey="backendRpm" value={sections.backend.requestsPerMinute ?? '—'} icon={Activity} />
              <OperationsStatCard label="Latency" infoKey="backendLatency" value={sections.backend.latencyMs != null ? `${Math.round(sections.backend.latencyMs)} ms` : '—'} icon={Timer} />
              <OperationsStatCard label="Instances" infoKey="backendInstances" value={sections.backend.runningInstances ?? '—'} icon={Server} />
              <OperationsStatCard label="5xx errors" infoKey="backend5xx" value={sections.backend.count5xx ?? '—'} icon={AlertCircle} accent="text-red-500" />
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <ResourceBarChart
                title="Cloud Run resources"
                infoKey="cloudRunResources"
                items={[
                  { name: 'CPU', value: sections.backend.cpuUsagePercent },
                  { name: 'Memory', value: sections.backend.memoryUsagePercent },
                ]}
              />
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <OpsChartTitle title="HTTP errors (1h)" infoKey="httpErrorsChart" />
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: '4xx', value: sections.backend.count4xx ?? 0, fill: '#f59e0b' },
                        { name: '5xx', value: sections.backend.count5xx ?? 0, fill: '#ef4444' },
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {[0, 1].map((i) => (
                          <Cell key={i} fill={i === 0 ? '#f59e0b' : '#ef4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <DataTable
              headers={['Field', 'Value']}
              columnKeys={['field', 'value']}
              rows={[
                ['Status', <StatusBadgeInline status={sections.backend.status} />],
                ['Environment', sections.backend.source || 'cloud_run'],
                ['Revision', sections.backend.latestRevision],
                ['Commit', sections.backend.currentCommitSha || '—'],
                ['Deployment', sections.backend.deploymentTime ? new Date(sections.backend.deploymentTime).toLocaleString() : '—'],
                ['URI', sections.backend.uri || '—'],
              ]}
            />
          </div>
        );

      case 'database':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <OperationsStatCard label="Query time" infoKey="dbQueryTime" value={sections.database.queryResponseTimeFormatted || '—'} icon={Timer} />
              <OperationsStatCard label="Connections" infoKey="dbConnections" value={sections.database.connections ?? '—'} icon={Database} />
              <OperationsStatCard label="Deadlocks" infoKey="dbDeadlocks" value={sections.database.deadlocks ?? '—'} icon={AlertCircle} />
              <OperationsStatCard label="Pending migrations" infoKey="pendingMigrations" value={sections.database.pendingMigrations.length} icon={HardDrive} accent={sections.database.pendingMigrations.length ? 'text-red-500' : undefined} />
            </div>
            <ResourceBarChart
              title="Cloud SQL utilization"
              infoKey="cloudSqlUtilization"
              items={[
                { name: 'CPU', value: sections.database.cpuUsagePercent },
                { name: 'Memory', value: sections.database.memoryUsagePercent },
                { name: 'Disk', value: sections.database.storageUsedPercent },
              ]}
            />
            {sections.database.pendingMigrations.length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
                <p className="font-semibold text-red-800 dark:text-red-200">Pending migrations</p>
                <ul className="mt-2 list-disc pl-5 text-sm text-red-900 dark:text-red-100">
                  {sections.database.pendingMigrations.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </div>
            )}
            <DataTable
              headers={['Slow query', 'Executions', 'Avg ms']}
              columnKeys={['slowQuery', 'executions', 'avgMs']}
              rows={(sections.database.slowQueries || []).map((q) => [q.query, q.executions, q.avgMs])}
              emptyMessage="No slow query data"
            />
          </div>
        );

      case 'errors': {
        if (detailViewError) {
          return (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Top Navigation Bar */}
              <div className="flex items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 rounded-2xl shadow-sm">
                <button
                  type="button"
                  onClick={() => setDetailViewError(null)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to System Error Logs
                </button>
                <div className="flex items-center gap-2">
                  {detailViewError.userId && (
                    <button
                      type="button"
                      onClick={() => handleNavigateProfile(detailViewError.userId, detailViewError.userRole)}
                      className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-950/50 dark:text-primary-300 flex items-center gap-1.5"
                    >
                      <User className="h-3.5 w-3.5" /> View User / Vendor Profile <ExternalLink className="h-3 w-3" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(detailViewError.message);
                      toast.success('Error message copied to clipboard!');
                    }}
                    className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 flex items-center gap-1.5"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy Message
                  </button>
                </div>
              </div>

              {/* Error Detail Card */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900 space-y-6">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300">
                      {detailViewError.statusCode === '0' ? 'NETWORK_OFFLINE' : `HTTP ${detailViewError.statusCode || '500'}`}
                    </span>
                    <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">
                      {detailViewError.serviceCategory || detailViewError.category}
                    </span>
                    <span className="text-xs text-gray-500">
                      {detailViewError.platform} • {detailViewError.appName}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white break-words">
                    {detailViewError.message}
                  </h2>
                </div>

                {/* Metadata Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-gray-50 dark:bg-gray-800/60 p-4 rounded-xl text-xs">
                  <div>
                    <span className="text-gray-500 block mb-0.5">User / Vendor ID</span>
                    {detailViewError.userId && detailViewError.userId.startsWith('SESS_') ? (
                      <span className="font-mono font-semibold text-gray-700 dark:text-gray-300 text-xs">
                        {detailViewError.userId} (Session)
                      </span>
                    ) : detailViewError.userId ? (
                      <button
                        type="button"
                        onClick={() => handleNavigateProfile(detailViewError.userId, detailViewError.userRole)}
                        className="font-bold text-primary-600 hover:underline flex items-center gap-1 text-sm"
                      >
                        {detailViewError.userId} <ExternalLink className="h-3 w-3" />
                      </button>
                    ) : (
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Anonymous Session</span>
                    )}
                  </div>

                  <div>
                    <span className="text-gray-500 block mb-0.5">User Role</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-200">
                      {detailViewError.userRole || 'USER'}
                    </span>
                  </div>

                  <div>
                    <span className="text-gray-500 block mb-0.5">Page Route</span>
                    <span className="font-mono text-gray-900 dark:text-gray-200 truncate block" title={detailViewError.pageRoute || '—'}>
                      {detailViewError.pageRoute || '—'}
                    </span>
                  </div>

                  <div>
                    <span className="text-gray-500 block mb-0.5">Timestamp</span>
                    <span className="font-medium text-gray-900 dark:text-gray-200">
                      {detailViewError.timestamp ? new Date(String(detailViewError.timestamp)).toLocaleString() : '—'}
                    </span>
                  </div>
                </div>

                {/* Audit Status Updater */}
                {detailViewError.id && (
                  <div className="flex items-center gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Audit Status:</span>
                    <button
                      type="button"
                      disabled={updatingStatusId === detailViewError.id}
                      onClick={() => handleStatusChange(detailViewError.id!, 'UNRESOLVED')}
                      className={`px-3 py-1 text-xs font-semibold rounded-lg ${
                        detailViewError.status === 'UNRESOLVED' || !detailViewError.status
                          ? 'bg-red-600 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      Unresolved
                    </button>
                    <button
                      type="button"
                      disabled={updatingStatusId === detailViewError.id}
                      onClick={() => handleStatusChange(detailViewError.id!, 'INVESTIGATING')}
                      className={`px-3 py-1 text-xs font-semibold rounded-lg ${
                        detailViewError.status === 'INVESTIGATING'
                          ? 'bg-sky-600 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      Investigating
                    </button>
                    <button
                      type="button"
                      disabled={updatingStatusId === detailViewError.id}
                      onClick={() => handleStatusChange(detailViewError.id!, 'RESOLVED')}
                      className={`px-3 py-1 text-xs font-semibold rounded-lg ${
                        detailViewError.status === 'RESOLVED'
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      Resolved
                    </button>
                  </div>
                )}

                {/* Stack Trace */}
                {detailViewError.stackTrace && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Stack Trace</h4>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(detailViewError.stackTrace || '');
                          toast.success('Stack trace copied to clipboard!');
                        }}
                        className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1"
                      >
                        <Copy className="h-3 w-3" /> Copy Stack Trace
                      </button>
                    </div>
                    <pre className="max-h-72 overflow-x-auto rounded-xl bg-gray-950 p-4 text-[11px] font-mono text-red-400 leading-relaxed shadow-inner">
                      {detailViewError.stackTrace}
                    </pre>
                  </div>
                )}

                {/* Device Info */}
                {detailViewError.deviceInfo && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Device Info Snapshot</h4>
                    <pre className="max-h-48 overflow-x-auto rounded-xl bg-gray-900 p-3.5 text-[11px] font-mono text-sky-300">
                      {JSON.stringify(detailViewError.deviceInfo, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Payload Snapshot */}
                {detailViewError.payloadSnapshot && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Request Payload Snapshot</h4>
                    <pre className="max-h-48 overflow-x-auto rounded-xl bg-gray-900 p-3.5 text-[11px] font-mono text-emerald-300">
                      {JSON.stringify(detailViewError.payloadSnapshot, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          );
        }

        const errorRows = errorLogsList.length > 0 ? errorLogsList : (filteredErrors || sections.runtimeErrors).latest || [];
        const totalCount = totalErrorsCount || errorRows.length;
        const unresolvedCount = errorRows.filter((r) => !r.status || r.status === 'UNRESOLVED').length;
        const offlineCount = errorRows.filter((r) => r.isOfflineReplay || r.statusCode === '0').length;
        const server500Count = errorRows.filter((r) => r.statusCode === '500').length;

        return (
          <div className="space-y-6">
            {/* Summary Metrics */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Total Errors</span>
                  <AlertCircle className="h-4 w-4 text-red-500" />
                </div>
                <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">{totalCount}</div>
                <p className="mt-1 text-xs text-gray-500">Matching selected criteria</p>
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Unresolved</span>
                  <Clock className="h-4 w-4 text-amber-600" />
                </div>
                <div className="mt-2 text-2xl font-bold text-amber-900 dark:text-amber-200">{unresolvedCount}</div>
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">Action required</p>
              </div>

              <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-4 dark:border-sky-900/40 dark:bg-sky-950/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-sky-700 dark:text-sky-400">Offline Replayed</span>
                  <WifiOff className="h-4 w-4 text-sky-600" />
                </div>
                <div className="mt-2 text-2xl font-bold text-sky-900 dark:text-sky-200">{offlineCount}</div>
                <p className="mt-1 text-xs text-sky-700 dark:text-sky-400">Queued during offline state</p>
              </div>

              <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 dark:border-purple-900/40 dark:bg-purple-950/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-purple-700 dark:text-purple-400">HTTP 500 Errors</span>
                  <Server className="h-4 w-4 text-purple-600" />
                </div>
                <div className="mt-2 text-2xl font-bold text-purple-900 dark:text-purple-200">{server500Count}</div>
                <p className="mt-1 text-xs text-purple-700 dark:text-purple-400">Backend Exceptions</p>
              </div>
            </div>

            {/* System Error Audit Filters - SINGLE HORIZONTAL SCROLLABLE ROW */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 space-y-2">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-primary-600" />
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    System Error Audit Filters
                  </span>
                </div>
                {(filterPlatform !== 'ALL' || filterAppName !== 'ALL' || errorCategory !== 'ALL' || filterStatusCode !== 'ALL' || filterStatus !== 'ALL' || filterUserSearch || errorSearch) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilterPlatform('ALL');
                      setFilterAppName('ALL');
                      setErrorCategory('ALL');
                      setFilterStatusCode('ALL');
                      setFilterStatus('ALL');
                      setFilterUserSearch('');
                      setErrorSearch('');
                      setErrorRange('24h');
                    }}
                    className="text-xs font-semibold text-red-600 hover:text-red-700 dark:text-red-400 flex items-center gap-1"
                  >
                    <X className="h-3.5 w-3.5" /> Clear All Filters
                  </button>
                )}
              </div>

              {/* Single Horizontal Scrollable Row for All Filters */}
              <div className="flex items-center gap-2.5 overflow-x-auto pb-2 pt-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 whitespace-nowrap min-w-full">
                {/* Platform */}
                <select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)} className={`${opsFilterClass} min-w-[140px]`}>
                  <option value="ALL">All Platforms</option>
                  <option value="WEB">Web</option>
                  <option value="ANDROID">Android Mobile</option>
                  <option value="IOS">iOS Mobile</option>
                  <option value="BACKEND">Backend</option>
                </select>

                {/* App / Panel */}
                <select value={filterAppName} onChange={(e) => setFilterAppName(e.target.value)} className={`${opsFilterClass} min-w-[170px]`}>
                  <option value="ALL">All Apps & Panels</option>
                  <option value="USER_APP">Vedika Health User App</option>
                  <option value="PLUS_APP">Vedika Health Plus Doctor App</option>
                  <option value="WEB_USER_PANEL">Web User Panel</option>
                  <option value="WEB_PROVIDER_PANEL">Web Service Provider Panel</option>
                  <option value="ADMIN_PANEL">Master Admin Panel</option>
                  <option value="BACKEND_API">Backend Express API</option>
                </select>

                {/* Service Category */}
                <select value={errorCategory} onChange={(e) => setErrorCategory(e.target.value)} className={`${opsFilterClass} min-w-[160px]`}>
                  <option value="ALL">All Services</option>
                  <option value="Doctor Registration">Doctor Registration</option>
                  <option value="Ambulance Booking">Ambulance Booking</option>
                  <option value="Auth">Authentication & Login</option>
                  <option value="Storage">File & Document Storage</option>
                  <option value="Payment">Razorpay / Payment</option>
                  <option value="Database">Database Query</option>
                  <option value="Network">Network Offline</option>
                  <option value="General">General UI & Exception</option>
                </select>

                {/* Status Code */}
                <select value={filterStatusCode} onChange={(e) => setFilterStatusCode(e.target.value)} className={`${opsFilterClass} min-w-[140px]`}>
                  <option value="ALL">All Status Codes</option>
                  <option value="500">500 Server Error</option>
                  <option value="400">400 Bad Request</option>
                  <option value="401">401 Unauthorized</option>
                  <option value="403">403 Forbidden</option>
                  <option value="404">404 Not Found</option>
                  <option value="0">0 (Network Offline)</option>
                </select>

                {/* Audit Status */}
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`${opsFilterClass} min-w-[150px]`}>
                  <option value="ALL">All Audit Statuses</option>
                  <option value="UNRESOLVED">Unresolved</option>
                  <option value="INVESTIGATING">Investigating</option>
                  <option value="RESOLVED">Resolved</option>
                </select>

                {/* Time Range */}
                <select value={errorRange} onChange={(e) => setErrorRange(e.target.value)} className={`${opsFilterClass} min-w-[130px]`}>
                  <option value="1h">Last 1 Hour</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="all">All Time</option>
                </select>

                {/* User / Vendor ID Search */}
                <input
                  type="text"
                  value={filterUserSearch}
                  onChange={(e) => setFilterUserSearch(e.target.value)}
                  placeholder="User / Vendor ID..."
                  className={`${opsFilterClass} min-w-[180px]`}
                />

                {/* Route / Message Search */}
                <input
                  type="text"
                  value={errorSearch}
                  onChange={(e) => setErrorSearch(e.target.value)}
                  placeholder="Search message or route..."
                  className={`${opsFilterClass} min-w-[180px]`}
                />
              </div>
            </div>

            {/* Error Data Table - Column 1 is User / Vendor ID */}
            <DataTable
              headers={['User / Vendor ID', 'Time', 'Platform & App', 'Service & Route', 'HTTP Status', 'Error Message', 'Audit Status', 'Action']}
              columnKeys={['user', 'time', 'app', 'route', 'code', 'message', 'status', 'action']}
              rows={errorRows.map((err) => [
                // Column 1: User or Vendor ID with Navigation
                <div key="user" className="flex flex-col">
                  {err.userId && err.userId.startsWith('SESS_') ? (
                    <span className="font-mono font-semibold text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1" title="Anonymous Session ID">
                      <User className="h-3 w-3 text-gray-400" /> {err.userId}
                    </span>
                  ) : err.userId ? (
                    <button
                      type="button"
                      onClick={() => handleNavigateProfile(err.userId, err.userRole)}
                      className="font-bold text-xs text-primary-600 hover:text-primary-700 hover:underline dark:text-primary-400 flex items-center gap-1"
                      title="Click to view profile page"
                    >
                      {err.userId} <ExternalLink className="h-3 w-3 shrink-0" />
                    </button>
                  ) : (
                    <span className="font-semibold text-xs text-gray-700 dark:text-gray-300">
                      Anonymous Session
                    </span>
                  )}
                  <span className="text-gray-500 text-[10px] uppercase font-mono">{err.userRole || 'USER'}</span>
                </div>,

                <div key="time" className="whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
                  {err.timestamp ? new Date(String(err.timestamp)).toLocaleString() : '—'}
                  {err.isOfflineReplay && (
                    <span className="ml-1 inline-flex items-center text-[10px] bg-sky-100 text-sky-800 px-1 rounded dark:bg-sky-900/50 dark:text-sky-300">
                      Offline
                    </span>
                  )}
                </div>,

                <div key="app" className="flex flex-col text-xs">
                  <span className="font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                    {err.platform === 'WEB' ? <Globe className="h-3 w-3 text-blue-500" /> : <Smartphone className="h-3 w-3 text-emerald-500" />}
                    {err.appName || 'WEB_USER_PANEL'}
                  </span>
                  <span className="text-gray-500 text-[10px]">{err.platform || 'WEB'}</span>
                </div>,

                <div key="route" className="flex flex-col text-xs max-w-xs">
                  <span className="font-medium text-primary-600 dark:text-primary-400">{err.serviceCategory || err.category}</span>
                  <span className="text-gray-500 truncate text-[11px]" title={err.pageRoute || '—'}>
                    {err.pageRoute || '—'}
                  </span>
                </div>,

                <div key="code" className="whitespace-nowrap">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      err.statusCode === '500'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                        : err.statusCode === '0'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                    }`}
                  >
                    {err.statusCode === '0' ? 'OFFLINE' : `HTTP ${err.statusCode || '500'}`}
                  </span>
                </div>,

                <div key="msg" className="flex items-center gap-1.5 max-w-xs">
                  <span className="line-clamp-2 text-xs text-gray-800 dark:text-gray-200 flex-1" title={err.message}>
                    {err.message}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(err.message);
                      toast.success('Error message copied to clipboard!');
                    }}
                    className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
                    title="Copy Error Message"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>,

                // Audit Status Column - Interactive In-Table Selection
                <div key="status" className="whitespace-nowrap">
                  <select
                    value={err.status || 'UNRESOLVED'}
                    onChange={(e) => handleStatusChange(err.id!, e.target.value)}
                    disabled={updatingStatusId === err.id}
                    className={`text-xs font-semibold px-2 py-1 rounded-lg border outline-none cursor-pointer transition-colors ${
                      err.status === 'RESOLVED'
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800'
                        : err.status === 'INVESTIGATING'
                        ? 'bg-sky-50 text-sky-800 border-sky-300 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800'
                        : 'bg-red-50 text-red-800 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800'
                    }`}
                  >
                    <option value="UNRESOLVED">Unresolved</option>
                    <option value="INVESTIGATING">Investigating</option>
                    <option value="RESOLVED">Resolved</option>
                  </select>
                </div>,

                <div key="action" className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setDetailViewError(err)}
                    className="px-2.5 py-1 text-xs font-medium rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-950/50 dark:text-primary-300 flex items-center gap-1"
                  >
                    <Eye className="h-3.5 w-3.5" /> Details
                  </button>
                </div>,
              ])}
              emptyMessage="No system error logs match the selected filter criteria."
            />

            {/* Pagination & Load More Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                Showing {errorRows.length} of {totalCount} total error logs
              </span>

              {hasMoreErrors && (
                <button
                  type="button"
                  disabled={loadingMoreErrors}
                  onClick={() => loadErrors(errorPage + 1, true)}
                  className={`${opsPrimaryBtn} text-xs px-5 py-2 flex items-center gap-2`}
                >
                  {loadingMoreErrors ? (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Loading more errors...
                    </>
                  ) : (
                    <>
                      Load More Errors ({totalCount - errorRows.length} remaining)
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      }

      case 'apis':
        return (
          <div className="space-y-6">
            <ApiLatencyBarChart title="Endpoint response time" endpoints={sections.internalApis.endpoints} infoKey="endpointLatency" />
            <DataTable
              headers={['Endpoint', 'Method', 'Status', 'ms', 'HTTP']}
              columnKeys={['endpoint', 'method', 'status', 'latency', 'http']}
              rows={sections.internalApis.endpoints.map((e) => [
                e.name,
                e.method,
                <StatusBadgeInline status={e.status} />,
                e.responseTimeMs,
                e.httpStatus ?? '—',
              ])}
            />
          </div>
        );

      case 'external':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <ServiceHealthPie
                title="External services"
                infoKey="externalHealthPie"
                items={sections.externalServices.services.map((s) => ({ name: s.name, status: s.status }))}
              />
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <OpsChartTitle title="Latency (ms)" infoKey="externalLatency" />
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={sections.externalServices.services
                        .filter((s) => s.latencyMs != null)
                        .map((s) => ({ name: s.name.split(' ')[0], ms: s.latencyMs }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis unit="ms" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="ms" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <DataTable
              headers={['Service', 'Status', 'Latency', 'Error']}
              columnKeys={['service', 'status', 'latency', 'message']}
              rows={sections.externalServices.services.map((s) => [
                s.name,
                <StatusBadgeInline status={s.status} />,
                s.latencyMs != null ? `${s.latencyMs} ms` : '—',
                s.errorMessage || '—',
              ])}
            />
          </div>
        );

      case 'auth':
        return (
          <div className="space-y-6">
            <ServiceHealthPie title="Auth checks" infoKey="authChecks" items={sections.authentication.checks.map((c) => ({ name: c.name, status: c.status }))} />
            <DataTable
              headers={['Check', 'Status', 'Details']}
              columnKeys={['job', 'status', 'details']}
              rows={sections.authentication.checks.map((c) => [c.name, <StatusBadgeInline status={c.status} />, c.message])}
            />
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <NotificationChannelChart channels={sections.notifications.channels} />
            <div className="grid grid-cols-3 gap-3">
              <OperationsStatCard label="Pending" infoKey="notificationPending" value={sections.notifications.summary.pending} icon={Timer} />
              <OperationsStatCard label="Sent 24h" infoKey="notificationSent" value={sections.notifications.summary.sent} icon={CheckCircle2} accent="text-emerald-500" />
              <OperationsStatCard label="Failed 24h" infoKey="notificationFailed" value={sections.notifications.summary.failed} icon={AlertCircle} accent="text-red-500" />
            </div>
          </div>
        );

      case 'scheduler':
        return (
          <DataTable
            headers={['Job', 'Schedule', 'Status', 'Enabled']}
            columnKeys={['job', 'schedule', 'status', 'enabled']}
            rows={sections.scheduler.jobs.map((job) => [
              String(job.name || job.id),
              String(job.schedule || 'Cloud Scheduler'),
              <StatusBadgeInline status={String(job.status)} />,
              job.enabled ? 'Yes' : 'No',
            ])}
          />
        );

      case 'queue':
        return (
          <div className="space-y-6">
            <QueueBarChart queues={sections.queue.queues} />
            <DataTable
              headers={['Queue', 'Pending', 'Running', 'Failed', 'Status']}
              columnKeys={['queue', 'pending', 'running', 'failed', 'status']}
              rows={sections.queue.queues.map((q) => [q.name, q.pending, q.running, q.failed, <StatusBadgeInline status={q.status} />])}
              emptyMessage="No queues"
            />
          </div>
        );

      case 'storage':
        return (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <OperationsStatCard label="Provider" infoKey="storageProvider" value={sections.storage.provider} icon={HardDrive} />
            <OperationsStatCard label="Bucket" infoKey="storageBucket" value={sections.storage.bucket || '—'} />
            <OperationsStatCard label="Latency" infoKey="storageLatency" value={sections.storage.latencyMs != null ? `${sections.storage.latencyMs} ms` : '—'} icon={Timer} />
            <OperationsStatCard label="Status" infoKey="storageHealth" value={<StatusBadgeInline status={sections.storage.status} />} />
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <ServiceHealthPie
              title="Credentials"
              infoKey="credentials"
              items={sections.security.credentialChecks.map((c) => ({
                name: c.name,
                status: c.loaded ? 'healthy' : 'critical',
              }))}
            />
            <DataTable
              headers={['Credential', 'Status', 'Loaded']}
              columnKeys={['credential', 'status', 'loaded']}
              rows={sections.security.credentialChecks.map((c) => [
                c.name,
                <StatusBadgeInline status={c.status} />,
                c.loaded ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : 'No',
              ])}
            />
          </div>
        );

      case 'analytics':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <CategoryPieChart title="Errors by category" data={categoryChartData} infoKey="errorAnalyticsPie" />
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <OpsChartTitle title="Top errors" infoKey="topErrorsBar" />
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={sections.errorAnalytics.groupedByFrequency.slice(0, 6).map((e, i) => ({
                        name: e.category,
                        count: e.occurrences,
                        fill: PIE_PALETTE[i % PIE_PALETTE.length],
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {sections.errorAnalytics.groupedByFrequency.slice(0, 6).map((_, i) => (
                          <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <DataTable
              headers={['Message', 'Category', 'Count', 'Latest']}
              columnKeys={['message', 'category', 'count', 'time']}
              rows={sections.errorAnalytics.groupedByFrequency.map((e) => [
                <span className="line-clamp-2 max-w-lg">{e.message}</span>,
                e.category,
                e.occurrences,
                new Date(e.latestTimestamp).toLocaleString(),
              ])}
            />
          </div>
        );

      case 'logs':
        return (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <input value={logSearch} onChange={(e) => setLogSearch(e.target.value)} placeholder="Search logs…" className={opsFilterClass} />
              <button type="button" onClick={downloadLogs} className={opsSecondaryBtn}>
                <Download className="h-4 w-4" /> Export
              </button>
            </div>
            {logsNotice && <p className="text-sm text-amber-600">{logsNotice}</p>}
            <div className="flex items-center gap-2">
              <OpsChartTitle title="Live log stream" infoKey="liveLogsViewer" />
            </div>
            <div className={opsLogViewer}>
              {liveLogs.length === 0 ? (
                <p className="text-gray-500">No logs in range</p>
              ) : (
                liveLogs.map((log, idx) => (
                  <div key={idx} className="mb-2 border-b border-gray-800 pb-2">
                    <span className="text-gray-500">[{String(log.timestamp || '')}] </span>
                    <span className="text-amber-400">{String(log.severity || 'INFO')} </span>
                    <span>{String(log.message || log.textPayload || '')}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case 'deployments':
        return (
          <DataTable
            headers={['Revision', 'Created', 'Commit', 'Status', 'Action']}
            columnKeys={['revision', 'created', 'commit', 'status', 'action']}
            rows={sections.deployments.history.map((d, idx) => [
              d.revision,
              d.createdAt ? new Date(d.createdAt).toLocaleString() : '—',
              d.commitSha || '—',
              d.status,
              idx > 0 ? (
                <button type="button" onClick={() => handleRollback(d.revision)} className="rounded-lg bg-amber-600 px-3 py-1 text-xs text-white hover:bg-amber-700">
                  Rollback
                </button>
              ) : (
                'Current'
              ),
            ])}
            emptyMessage={meta?.localMode ? 'Requires Cloud Run (production)' : 'No deployments'}
          />
        );

      case 'uptime':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <UptimeDonut label="24 hours" availabilityPercent={sections.uptime.last24Hours.availabilityPercent} totalRequests={sections.uptime.last24Hours.totalRequests} />
              <UptimeDonut label="7 days" availabilityPercent={sections.uptime.last7Days.availabilityPercent} totalRequests={sections.uptime.last7Days.totalRequests} />
              <UptimeDonut label="30 days" availabilityPercent={sections.uptime.last30Days.availabilityPercent} totalRequests={sections.uptime.last30Days.totalRequests} />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={opsPageWrap}>
      <OperationsMobileTabs activeTab={activeTab} onTabChange={setActiveTab} />
      <div className={opsShell}>
        <OperationsSidebar activeTab={activeTab} onTabChange={setActiveTab} />
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <OperationsPanelShell
            title={tabMeta.title}
            tabInfoKey={activeTab}
            description={
              lastUpdated
                ? `${tabMeta.description} · Updated ${lastUpdated.toLocaleTimeString()}`
                : tabMeta.description
            }
            actions={refreshBtn}
          >
            {renderPanel()}
          </OperationsPanelShell>
        </main>
      </div>

      {/* Error Detail Modal */}
      {selectedErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900 dark:border dark:border-gray-800 space-y-5">
            <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-800 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300">
                    {selectedErrorModal.statusCode === '0' ? 'NETWORK_OFFLINE' : `HTTP ${selectedErrorModal.statusCode || '500'}`}
                  </span>
                  <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">
                    {selectedErrorModal.serviceCategory || selectedErrorModal.category}
                  </span>
                  <span className="text-xs text-gray-500">
                    {selectedErrorModal.platform} • {selectedErrorModal.appName}
                  </span>
                </div>
                <h3 className="mt-2 text-lg font-bold text-gray-900 dark:text-white break-words">
                  {selectedErrorModal.message}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedErrorModal(null)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Meta Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 dark:bg-gray-800/50 p-3.5 rounded-xl text-xs">
              <div>
                <span className="text-gray-500 block">Occurred At</span>
                <span className="font-medium text-gray-900 dark:text-gray-200">
                  {selectedErrorModal.timestamp ? new Date(String(selectedErrorModal.timestamp)).toLocaleString() : '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Page / Route</span>
                <span className="font-medium text-gray-900 dark:text-gray-200 truncate block" title={selectedErrorModal.pageRoute || '—'}>
                  {selectedErrorModal.pageRoute || '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">User / Vendor ID</span>
                <span className="font-medium text-gray-900 dark:text-gray-200">
                  {selectedErrorModal.userId || 'Guest'} ({selectedErrorModal.userRole || 'USER'})
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Audit Status</span>
                <span className="font-bold text-primary-600 dark:text-primary-400">
                  {selectedErrorModal.status || 'UNRESOLVED'}
                </span>
              </div>
            </div>

            {/* Status Update Actions */}
            {selectedErrorModal.id && (
              <div className="flex items-center gap-2 pt-1 border-t border-gray-100 dark:border-gray-800">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Mark Audit Status:</span>
                <button
                  type="button"
                  disabled={updatingStatusId === selectedErrorModal.id}
                  onClick={() => handleStatusChange(selectedErrorModal.id!, 'INVESTIGATING')}
                  className="px-3 py-1 text-xs font-semibold rounded-lg bg-sky-100 text-sky-800 hover:bg-sky-200 dark:bg-sky-950 dark:text-sky-300"
                >
                  Investigating
                </button>
                <button
                  type="button"
                  disabled={updatingStatusId === selectedErrorModal.id}
                  onClick={() => handleStatusChange(selectedErrorModal.id!, 'RESOLVED')}
                  className="px-3 py-1 text-xs font-semibold rounded-lg bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-950 dark:text-emerald-300"
                >
                  Resolved
                </button>
                <button
                  type="button"
                  disabled={updatingStatusId === selectedErrorModal.id}
                  onClick={() => handleStatusChange(selectedErrorModal.id!, 'UNRESOLVED')}
                  className="px-3 py-1 text-xs font-semibold rounded-lg bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-950 dark:text-red-300"
                >
                  Unresolved
                </button>
              </div>
            )}

            {/* Stack Trace */}
            {selectedErrorModal.stackTrace && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Stack Trace</h4>
                <pre className="max-h-60 overflow-x-auto rounded-xl bg-gray-950 p-4 text-[11px] font-mono text-red-400">
                  {selectedErrorModal.stackTrace}
                </pre>
              </div>
            )}

            {/* Device Specs */}
            {selectedErrorModal.deviceInfo && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Device Info Snapshot</h4>
                <pre className="max-h-40 overflow-x-auto rounded-xl bg-gray-900 p-3 text-[11px] font-mono text-sky-300">
                  {JSON.stringify(selectedErrorModal.deviceInfo, null, 2)}
                </pre>
              </div>
            )}

            {/* Payload Snapshot */}
            {selectedErrorModal.payloadSnapshot && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Request Payload Snapshot</h4>
                <pre className="max-h-40 overflow-x-auto rounded-xl bg-gray-900 p-3 text-[11px] font-mono text-emerald-300">
                  {JSON.stringify(selectedErrorModal.payloadSnapshot, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationsDashboardPage;
